import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { AdminPageHeader, AdminTableCard, useAdminList } from './AdminUi';
import './admin.css';

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  phone_number: '',
  gender: 'M',
  address: '',
  age: '',
  height: '',
  weight: '',
  dateofbirth: '',
  disease_description: '',
};

export default function AdminUsers() {
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const { data, loading, error, load } = useAdminList(
    () => api.get('admin/users/', { params: { q: q || undefined, limit: 100 } }),
    []
  );

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const closeAdd = () => {
    setShowAdd(false);
    setForm(EMPTY_FORM);
  };

  const handleDelete = async (u) => {
    const label = u.name || u.email || `#${u.id}`;
    if (
      !window.confirm(
        `Delete patient "${label}"?\n\nThis will also remove their appointments, payments, prescriptions, and related data. This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`admin/users/${u.id}/`);
      toast.success('Patient deleted successfully');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete patient');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone_number: form.phone_number.trim(),
        gender: form.gender,
        address: form.address.trim(),
        age: form.age,
        height: form.height,
        weight: form.weight,
        dateofbirth: form.dateofbirth,
      };
      if (form.disease_description.trim()) {
        payload.disease_description = form.disease_description.trim();
      }

      await api.post('admin/users/', payload);
      toast.success('Patient created successfully');
      closeAdd();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create patient');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <AdminPageHeader title="Patients" subtitle={`${data.total} registered users`}>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-primary rounded-pill px-3"
            onClick={() => setShowAdd(true)}
          >
            <i className="bi bi-plus-lg me-1" />
            Add Patient
          </button>
          <form
            className="d-flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
          >
            <input
              className="form-control"
              style={{ minWidth: 220 }}
              placeholder="Search name, email, phone…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className="btn btn-outline-primary rounded-pill px-3" type="submit">
              Search
            </button>
          </form>
        </div>
      </AdminPageHeader>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : (
        <AdminTableCard>
          <table className="admin-table admin-table-people">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Contact</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Joined</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.results.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted py-4">No users found</td></tr>
              ) : (
                data.results.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="admin-person-cell">
                        <div className="admin-person-avatar">
                          {u.image ? (
                            <img src={u.image} alt="" />
                          ) : (
                            (u.name || 'U').slice(0, 1).toUpperCase()
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="fw-semibold text-truncate">{u.name}</div>
                          <div className="small text-muted">ID #{u.id}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="small fw-semibold text-truncate" style={{ maxWidth: 220 }}>{u.email}</div>
                      <div className="small text-muted">{u.phone_number || '—'}</div>
                    </td>
                    <td>{u.age ?? '—'}</td>
                    <td>{u.gender || '—'}</td>
                    <td className="small text-muted">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <div className="admin-row-actions">
                        <Link
                          to={`/admin/users/${u.id}`}
                          className="admin-action-btn view"
                          title="View profile"
                        >
                          <i className="bi bi-eye" />
                          <span>View</span>
                        </Link>
                        <Link
                          to={`/admin/users/${u.id}?edit=1`}
                          className="admin-action-btn view"
                          title="Edit profile"
                          style={{ color: '#0f766e', borderColor: '#99f6e4', background: '#f0fdfa' }}
                        >
                          <i className="bi bi-pencil" />
                        </Link>
                        <button
                          type="button"
                          className="admin-action-btn reject"
                          title="Delete patient"
                          onClick={() => handleDelete(u)}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableCard>
      )}

      {showAdd &&
        createPortal(
          <div
            className="admin-add-doctor-overlay"
            onClick={closeAdd}
            role="presentation"
          >
            <div
              className="admin-add-doctor-dialog"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-patient-title"
            >
              <form onSubmit={handleCreate} className="admin-add-doctor-form">
                <div className="admin-add-doctor-header">
                  <div>
                    <h5 id="add-patient-title" className="fw-bold mb-0">
                      Add Patient
                    </h5>
                    <p className="small text-muted mb-0">
                      Create a patient account (login credentials will be emailed)
                    </p>
                  </div>
                  <button type="button" className="btn-close" onClick={closeAdd} aria-label="Close" />
                </div>

                <div className="admin-add-doctor-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Full name *</label>
                      <input
                        className="form-control"
                        name="name"
                        value={form.name}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Email *</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={form.email}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Password *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="password"
                        value={form.password}
                        onChange={onFormChange}
                        minLength={6}
                        required
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Phone *</label>
                      <input
                        className="form-control"
                        name="phone_number"
                        value={form.phone_number}
                        onChange={onFormChange}
                        maxLength={10}
                        inputMode="numeric"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Gender *</label>
                      <select
                        className="form-select"
                        name="gender"
                        value={form.gender}
                        onChange={onFormChange}
                        required
                      >
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Other</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Age *</label>
                      <input
                        type="number"
                        min="1"
                        max="150"
                        className="form-control"
                        name="age"
                        value={form.age}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Date of birth *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="dateofbirth"
                        value={form.dateofbirth}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Height (cm) *</label>
                      <input
                        type="number"
                        min="1"
                        step="0.1"
                        className="form-control"
                        name="height"
                        value={form.height}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Weight (kg) *</label>
                      <input
                        type="number"
                        min="1"
                        step="0.1"
                        className="form-control"
                        name="weight"
                        value={form.weight}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Address *</label>
                      <textarea
                        className="form-control"
                        name="address"
                        rows={2}
                        value={form.address}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Disease / notes</label>
                      <textarea
                        className="form-control"
                        name="disease_description"
                        rows={2}
                        value={form.disease_description}
                        onChange={onFormChange}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>

                <div className="admin-add-doctor-footer">
                  <button type="button" className="btn btn-light rounded-pill px-3" onClick={closeAdd}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={saving}>
                    {saving ? 'Creating…' : 'Create Patient'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
